class DiscountCode extends HTMLElement {
  constructor() {
    super();

    this.isApplyDiscountProgress = false;

    this.totalCartSelector = null;
    this.cartLineItems = null;
    this.cartDrawerWrapper = null;
  }

  connectedCallback() {
    this.discountCodeError = this.querySelector('.discount-code__error');
    this.discountCodeWrapper = this.querySelector('.discount-code__applied-wrapper');
    this.discountCodeValue = this.querySelector('.discount-code__applied-value');

    const cartDrawerWrapper = document.querySelector('cart-drawer');
    if (cartDrawerWrapper) {
      this.totalCartSelector = cartDrawerWrapper.querySelector('.totals__total-value');
      this.cartLineItems = cartDrawerWrapper.querySelectorAll('.cart-item');
    }

    this.addEventListeners();
    this.init();
  }

  addEventListeners() {
    this.applyBtn = this.querySelector('.discount-code__btn');
    this.applyBtn.addEventListener('click', (e) => this.handleApplyBtnClick(e));

    this.clearBtn = this.querySelector('.discount-code__clear-btn');
    this.clearBtn.addEventListener('click', (e) => this.handleClearBtnClick(e));

    this.discountCodeInput = this.querySelector('.discount-code__input');
    this.discountCodeInput.addEventListener('keyup', (e) => this.handleDiscountCodeInputKeyup(e));
  }

  async init() {
    const cartInfo = await this.getCartInfo();
    if (!cartInfo) return;

    const titles = [];
    cartInfo.items.forEach((each) => {
      each.line_level_discount_allocations.forEach((eachDiscount) => {
        const discountApplication = eachDiscount.discount_application;
        if (discountApplication && discountApplication.type === 'discount_code') {
          if (!titles.includes(discountApplication.title)) {
            titles.push(discountApplication.title);
          }
        }
      });
    });

    this.showAppliedDiscountCode(titles);

    if (!this.totalCartSelector || !cartInfo?.items_subtotal_price || !cartInfo?.original_total_price) return;
    if (cartInfo.items_subtotal_price !== cartInfo.original_total_price) {
      this.totalCartSelector.innerHTML =
        '<s class="old-price" aria-label="Original price: ' +
        Shopify.formatMoney(cartInfo.original_total_price) +
        '">' +
        Shopify.formatMoney(cartInfo.original_total_price) +
        '</s><span class="visually-hidden">Current price:</span>' +
        Shopify.formatMoney(cartInfo.items_subtotal_price);
    } else {
      this.totalCartSelector.innerHTML = Shopify.formatMoney(cartInfo.items_subtotal_price);
    }
  }

  async handleApplyBtnClick(e) {
    e.preventDefault();

    if (!this.discountCodeInput) return;

    await this.applyDiscount(this.discountCodeInput.value);

    this.resetApplyButton();
    this.clearDiscountCodeInput();
  }

  async handleClearBtnClick(e) {
    e.preventDefault();

    await this.clearDiscount();

    this.resetApplyButton();
    this.clearDiscountCodeInput();
  }

  async handleDiscountCodeInputKeyup(e) {
    e.preventDefault();

    if (e.key !== 'Enter') return;

    await this.applyDiscount(e.currentTarget.value);

    this.resetApplyButton();
    this.clearDiscountCodeInput();
  }

  async clearDiscount() {
    if (this.isApplyDiscountProgress) return;

    this.showLoadingApplyButton();

    await fetch(`${Shopify.routes.root}discount/clear`);

    const cartInfo = await this.getCartInfo();
    if (!cartInfo) return;

    this.clearCartLineItemsPrice(cartInfo);

    if (this.totalCartSelector) {
      this.totalCartSelector.innerHTML = Shopify.formatMoney(cartInfo.items_subtotal_price);
    }

    if (this.discountCodeWrapper) this.discountCodeWrapper.style.display = 'none';

    this.discountCodeValue.innerHTML = '';
    this.discountCodeError.innerHTML = '';
  }

  clearCartLineItemsPrice(cartInfo) {
    if (!this.cartLineItems) return;

    this.cartLineItems.forEach((eachLineItem, index) => {
      const discountedPriceWrapper = eachLineItem.querySelector('.cart-item__discounted-prices');
      if (discountedPriceWrapper) {
        const oldPriceWrapper = discountedPriceWrapper.querySelector('.cart-item__old-price');
        if (oldPriceWrapper) {
          oldPriceWrapper.innerHTML = '';
        }

        const finalPriceWrapper = discountedPriceWrapper.querySelector('.cart-item__final-price');
        if (finalPriceWrapper) {
          finalPriceWrapper.innerHTML = Shopify.formatMoney(cartInfo.items[index].final_line_price);
        }
      } else {
        const productOptionWrapper = eachLineItem.querySelector('.product-option');
        if (productOptionWrapper && index < cartInfo.items.length) {
          productOptionWrapper.innerHTML = Shopify.formatMoney(cartInfo.items[index].final_line_price);
        }
      }
    });
  }

  async getAuthToken(logError = true) {
    try {
      const response = await fetch('/payments/config', { method: 'GET' });
      if (!response.ok) {
        this.discountCodeError.innerHTML = this.dataset.errorAuthToken;
        throw new Error(this.dataset.errorAuthToken);
      }
      const data = await response.json();
      const auth_token = btoa(data.paymentInstruments.accessToken);

      return auth_token;
    } catch (error) {
      if (logError) {
        console.error(`${this.dataset.errorAuthToken  }: `, error);
      }

      this.discountCodeError.innerHTML = this.dataset.errorAuthToken;
      return null;
    }
  }

  async getCartInfo(logError = true) {
    try {
      const response = await fetch('/cart.js', {});
      if (!response.ok) {
        this.discountCodeError.innerHTML = this.dataset.errorCartInfo;
        throw new Error(this.dataset.errorCartInfo);
      }
      const data = await response.json();

      return data;
    } catch (error) {
      if (logError) {
        console.error(`${this.dataset.errorCartInfo  }: `, error);
      }

      this.discountCodeError.innerHTML = this.dataset.errorCartInfo;
      return null;
    }
  }

  async validateDiscountCode(authToken, cartInfo, code, logError = true) {
    const body = {
      checkout: {
        country: Shopify.country,
        discount_code: code,
        line_items: cartInfo.items,
        presentment_currency: Shopify.currency.active,
      },
    };

    try {
      const response = await fetch('/wallets/checkouts/', {
        headers: {
          accept: '*/*',
          'cache-control': 'no-cache',
          authorization: `Basic ${  authToken}`,
          'content-type': 'application/json, text/javascript',
          pragma: 'no-cache',
          'sec-fetch-dest': 'empty',
          'sec-fetch-mode': 'cors',
          'sec-fetch-site': 'same-origin',
        },
        referrerPolicy: 'strict-origin-when-cross-origin',
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        body: JSON.stringify(body),
      });

      const data = await response.json();
      if (!response.ok) {
        let errCode = data?.errors?.applied_discount?.code[0]?.code;
        let errMsg = data?.errors?.applied_discount?.code[0]?.message;
        console.log(errMsg);
        if (!errCode || !errMsg) {
          const lineItem = data.errors?.line_items?.[0];
          if (lineItem?.variant_id?.[0]) {
            errCode = lineItem.variant_id[0].code;
            errMsg = lineItem.variant_id[0].message;
          } else {
            errCode = 'unknown_error';
            errMsg = this.dataset.enterValidDiscountCode;
          }
        }

        if (errCode === 'product_not_available') {
          errMsg = this.dataset.discountIncompatibleWithProduct
        }

        throw errMsg;
      }

      return data;
    } catch (error) {
      if (logError) {
        console.error(`${this.dataset.enterValidDiscountCode  }: `, error);
      }

      this.discountCodeError.innerHTML = error;
      return null;
    }
  }

  showAppliedDiscountCode(titles) {
    if (titles.length === 0) {
      this.discountCodeWrapper.style.display = 'none';
      return;
    }

    this.discountCodeWrapper.style.display = 'flex';

    // For now, let's assume we only apply one discount code. I think we will need to apply multiple discount codes soon.
    this.discountCodeValue.innerHTML = titles[0];
  }

  updateTotalPrice(checkoutInfo) {
    this.totalCartSelector.innerHTML =
      '<s class="old-price" aria-label="Original price: ' +
      Shopify.formatMoney(checkoutInfo.checkout.total_line_items_price) +
      '">' +
      Shopify.formatMoney(checkoutInfo.checkout.total_line_items_price) +
      '</s><span class="visually-hidden">Current price:</span>' +
      Shopify.formatMoney(checkoutInfo.checkout.total_price);
  }

  updateCartLineItemsPrice(cartInfo) {
    if (!this.cartLineItems) return;

    this.cartLineItems.forEach((eachLineItem, index) => {
      const discountedPriceWrapper = eachLineItem.querySelector('.cart-item__discounted-prices');
      if (discountedPriceWrapper) {
        const finalPriceWrapper = discountedPriceWrapper.querySelector('.cart-item__final-price');
        if (finalPriceWrapper) {
          finalPriceWrapper.innerHTML = Shopify.formatMoney(cartInfo.items[index].final_line_price);
        }

        const oldPriceWrapper = discountedPriceWrapper.querySelector('.cart-item__old-price');
        if (oldPriceWrapper) {
          oldPriceWrapper.innerHTML = Shopify.formatMoney(cartInfo.items[index].original_line_price);
        }
      } else {
        const productOptionWrapper = eachLineItem.querySelector('.product-option');
        if (productOptionWrapper) {
          productOptionWrapper.innerHTML =
            `<s class="old-price">${ 
            Shopify.formatMoney(cartInfo.items[index].original_line_price) 
            }</s>${ 
            Shopify.formatMoney(cartInfo.items[index].final_line_price)}`;
        }
      }
    });
  }

  showLoadingApplyButton() {
    this.isApplyDiscountProgress = true;

    if (!this.applyBtn) return;

    this.applyBtn.innerHTML = "<div class='loader'></div>";
    this.applyBtn.style.pointerEvents = 'none';
  }

  resetApplyButton() {
    if (!this.applyBtn) return;

    this.applyBtn.innerHTML = this.dataset.apply;
    this.applyBtn.style.pointerEvents = 'all';

    this.isApplyDiscountProgress = false;
  }

  clearDiscountCodeInput() {
    this.discountCodeInput.value = '';
  }

  async runDiscount(discountCodes) {
    const response = await fetch(`${Shopify.routes.root}checkout?discount=${discountCodes.join(',')}&_=${Date.now()}`);
    const html = await response.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const discountScript = doc.querySelector('script[data-discount-allocations]');

    if (discountScript) {
      const discountData = JSON.parse(discountScript.textContent);
      return { success: true, discounts: discountData };
    } 
      return { success: false, error: 'Failed to apply discount' };
    
  }

  async applyDiscount(code) {
    this.discountCodeError.innerHTML = '';

    if (!code || code.trim() === '') {
      this.discountCodeError.innerHTML = this.dataset.enterDiscountCode;
      return;
    }

    if (this.isApplyDiscountProgress) return;

    this.showLoadingApplyButton();

    const authToken = await this.getAuthToken();
    if (!authToken) return;

    const cartInfo = await this.getCartInfo();
    if (!cartInfo) return;

    const checkoutInfo = await this.validateDiscountCode(authToken, cartInfo, code);
    if (!checkoutInfo) return;

    if (checkoutInfo.checkout && checkoutInfo.checkout.applied_discounts.length > 0) {
      const discountCodes = [code];
      await this.runDiscount(discountCodes);

      const newCartInfo = await this.getCartInfo();
      if (!newCartInfo) return;

      this.updateCartLineItemsPrice(newCartInfo);

      this.showAppliedDiscountCode([checkoutInfo.checkout.applied_discounts[0].title]);

      this.updateTotalPrice(checkoutInfo);
    } else {
      this.discountCodeError.innerHTML = this.dataset.enterValidDiscountCode;
    }
  }
}

customElements.define('discount-code', DiscountCode);
